import i18next from 'i18next';
import {GetPath, history, OpenPage} from 'packages/history/history';
import {languages} from 'packages/localization/languages';
import {changeLanguage, useLocalization} from 'packages/localization/localization';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useLocation} from 'react-router-dom';
// components
import Dropdown from 'packages/dropdowns/dropdownBase.react';
import Logo from '../logo/logo.react';
// icons
import {ReactComponent as ArrowIcon} from 'src/icons/arrow-forward.svg';
import {ReactComponent as CrossIcon} from 'src/icons/cross.svg';
import {ReactComponent as FacebookIcon} from 'src/icons/facebook.svg';
import {ReactComponent as InstagramIcon} from 'src/icons/instagram.svg';
import {ReactComponent as LanguageIcon} from 'src/icons/language.svg';
import {ReactComponent as Menu} from 'src/icons/menu.svg';
import {ReactComponent as YoutubeIcon} from 'src/icons/youtube.svg';
// styles
import {CreateSheet, css} from 'aphrodite';
import {MakeButton} from 'src/themes/button';
import {
    Accent1,
    ElementBg,
    LightGray,
    PageBg,
    Primary,
    Secondary,
    StrokeColor,
    TextArea,
    TextColor,
    WhiteText,
} from 'src/themes/colors';
import {Sizes} from 'src/themes/layouts';
import {GetMainButtons} from './MainButtons';
import {session, SignOut} from 'packages/session/session';

export default function Header({
    active,
    isTransparentAbove,
    hideMainButtons,
}: {
    active?: string;
    isTransparentAbove?: string;
    hideMainButtons?: boolean;
}) {
    const [rightMenu, setRightMenu] = useState<boolean>(false);
    const [isHeaderTransparent, setIsHeaderTransparent] = useState(false);
    const headerRef = useRef<HTMLDivElement | null>(null);
    const rafId = useRef<number | null>(null);

    const location = useLocation();
    const {t, tReady} = useLocalization('header');

    const openRightMenu = () => {
        setRightMenu(true);
    };

    const closeRightMenu = () => {
        setRightMenu(false);
    };

    const checkTransparent = useCallback(() => {
        if (!tReady) return;
        if (!isTransparentAbove) {
            setIsHeaderTransparent(false);
            return;
        }

        const target = document.getElementById(isTransparentAbove);
        const headerElement = headerRef.current;

        if (!target || !headerElement) {
            setIsHeaderTransparent(false);
            return;
        }

        const scrollTop = window.scrollY ?? document.documentElement?.scrollTop ?? document.body?.scrollTop ?? 0;
        const targetRect = target.getBoundingClientRect();
        const targetTop = targetRect.top + scrollTop;
        const targetBottom = targetTop + targetRect.height;

        setIsHeaderTransparent(scrollTop < targetBottom);
    }, [tReady, isTransparentAbove]);

    const scheduleCheck = useCallback(() => {
        if (rafId.current != null) return;
        rafId.current = requestAnimationFrame(() => {
            rafId.current = null;
            checkTransparent();
        });
    }, [checkTransparent]);

    useEffect(() => {
        if (!isTransparentAbove) {
            setIsHeaderTransparent(false);
            return;
        }

        scheduleCheck();
        const id1 = requestAnimationFrame(scheduleCheck);
        const id2 = requestAnimationFrame(scheduleCheck);

        window.addEventListener('scroll', scheduleCheck, {passive: true});
        window.addEventListener('resize', scheduleCheck);

        return () => {
            cancelAnimationFrame(id1);
            cancelAnimationFrame(id2);
            window.removeEventListener('scroll', scheduleCheck as any);
            window.removeEventListener('resize', scheduleCheck as any);
        };
    }, [tReady, isTransparentAbove, scheduleCheck]);

    useEffect(() => {
        if (!isTransparentAbove) return;
        scheduleCheck();
    }, [location.pathname, isTransparentAbove, scheduleCheck]);

    if (!tReady) {
        return null;
    }

    return (
        <div
            ref={headerRef}
            className={css(Styles.header, isHeaderTransparent && Styles.transparent)}
        >
            <div className={css(Styles.container)}>
                <LogoHeader white={isHeaderTransparent} />
                {!isHeaderTransparent && !hideMainButtons ? (
                    <nav className={css(Styles.mainMenu, Styles.col, Styles.center)}>
                        {GetMainButtons(location).map((mainButton, key) => (
                            <a
                                key={key}
                                href={GetPath(mainButton.link)}
                                onClick={OpenPage(mainButton.link)}
                                className={css(MakeButton(mainButton.theme), Styles.btn)}
                            >
                                {t(mainButton.titleTPath)}
                            </a>
                        ))}
                    </nav>
                ) : null}
                <div className={css(Styles.col, Styles.right)}>
                    <div className={css(Styles.language)}>
                        <LanguagesMenu isTransparent={isHeaderTransparent} />
                    </div>
                    <div
                        className={css(Styles.menuToggle)}
                        onClick={openRightMenu}
                        id="open-menu-button"
                    >
                        <Menu
                            className={css(Styles.rightMenuIcon)}
                            width="22"
                            height="22"
                        />
                    </div>
                    <MenuRight
                        visible={rightMenu}
                        close={closeRightMenu}
                        active={active}
                    />
                </div>
            </div>
        </div>
    );
}

const LogoHeader = ({white}: {white?: boolean}) => {
    return (
        <div className={css(Styles.col, Styles.left)}>
            <Logo white={white} />
        </div>
    );
};

interface menuProps {
    visible: boolean;
    active?: string;
    close: () => void;
}

const MenuRight = (props: menuProps) => {
    const {t, tReady} = useLocalization('header');
    const {visible, close} = props;
    const [showContent, setShowContent] = useState(visible);

    const wrapper = useRef<HTMLDivElement>(null);

    useEffect(() => {
        function onClickOverlay(e: MouseEvent) {
            if (visible) {
                if (e.target instanceof HTMLElement && wrapper.current && e.target.id !== 'open-menu-button') {
                    if (!wrapper.current.contains(e.target)) {
                        close();
                    }
                }
            }
        }

        window.addEventListener('click', onClickOverlay);
        return () => {
            window.removeEventListener('click', onClickOverlay);
        };
    }, [visible, close]);

    useEffect(() => {
        if (visible) {
            setShowContent(true);
        } else {
            setTimeout(() => {
                setShowContent(false);
            }, 200);
        }
    }, [visible]);

    if (!tReady) {
        return null;
    }

    return (
        <div
            ref={wrapper}
            className={css(Styles.rightMenu, visible && Styles.rightMenuVisible)}
        >
            {showContent && (
                <>
                    <div
                        className={css(Styles.menuClose)}
                        onClick={close}
                    >
                        <CrossIcon
                            width="20"
                            height="20"
                            className={css(Styles.menuCloseIcon)}
                        />
                    </div>
                    <div className={css(Styles.menuTop)}>
                        <h4 className={css(Styles.rightMenuItem)}>{t('menu')}</h4>
                        <LanguagesMenu isMobile />
                    </div>
                    <MenuList close={close} />
                    {GetMainButtons().map((mainButton, idx) => (
                        <a
                            key={idx}
                            href={GetPath(mainButton.link)}
                            onClick={OpenPage(mainButton.link)}
                            className={css(
                                MakeButton(mainButton.theme),
                                Styles.rightMenuBtn,
                                idx === 0 && Styles.rightMenuBtnTop,
                                Styles.btn
                            )}
                        >
                            {t(mainButton.titleTPath)}
                        </a>
                    ))}
                    <div className={css(Styles.contact)}>
                        <p className={css(Styles.contactText)}>
                            <a
                                href="tel:+393429287530"
                                className={css(Styles.phone)}
                            >
                                +39 342 928 75 30
                            </a>
                        </p>
                        <p className={css(Styles.contactText)}>
                            <a
                                href="tel:hello@motoragazzi.com"
                                className={css(Styles.contactLink)}
                            >
                                hello@motoragazzi.com
                            </a>
                        </p>
                        <ul className={css(Styles.social)}>
                            <li className={css(Styles.socialItem)}>
                                <a
                                    {...{
                                        className: css(Styles.socialItemLink),
                                        href: 'https://www.youtube.com/channel/UCjNrhJZQP931J4e8SxapTyw',
                                        target: '_blank',
                                        rel: 'noopener noreferrer',
                                        'aria-label': 'Youtube',
                                    }}
                                >
                                    <YoutubeIcon
                                        className={css(Styles.socialItemIcon)}
                                        width="30"
                                        height="30"
                                    />
                                </a>
                            </li>
                            <li className={css(Styles.socialItem)}>
                                <a
                                    {...{
                                        className: css(Styles.socialItemLink),
                                        href: 'https://www.instagram.com/motoragazzi/',
                                        target: '_blank',
                                        rel: 'noopener noreferrer',
                                        'aria-label': '@motoragazzi',
                                    }}
                                >
                                    <InstagramIcon
                                        className={css(Styles.socialItemIcon)}
                                        width="30"
                                        height="30"
                                    />
                                </a>
                            </li>
                            <li className={css(Styles.socialItem)}>
                                <a
                                    {...{
                                        className: css(Styles.socialItemLink),
                                        href: 'https://www.facebook.com/motoragazzi',
                                        target: '_blank',
                                        rel: 'noopener noreferrer',
                                        'aria-label': 'Facebook',
                                    }}
                                >
                                    <FacebookIcon
                                        className={css(Styles.socialItemIcon)}
                                        width="30"
                                        height="30"
                                    />
                                </a>
                            </li>
                        </ul>
                    </div>
                </>
            )}
        </div>
    );
};

const LanguagesMenu = ({isMobile, isTransparent}: {isMobile?: boolean; isTransparent?: boolean}) => {
    const location = useLocation();

    const change = (code: string) => {
        const [, , ...restPathParts] = location.pathname.split('/');

        const newPath = `/${code}/${restPathParts.join('/')}`;

        history.push(newPath);
        changeLanguage(code);
    };

    return (
        <Dropdown
            {...{
                animation: 'fade',
                header: () => (
                    <div
                        className={css(
                            Styles.languageItem,
                            isTransparent && Styles.languageItemTransparent,
                            isMobile && Styles.languageMobile
                        )}
                    >
                        <LanguageIcon
                            className={css(Styles.languageIcon, isTransparent && Styles.languageIconTransparent)}
                            width="24"
                            height="24"
                        />
                        <span className={css(Styles.lngTitle)}>{i18next.language}</span>
                    </div>
                ),
                dropElement: () => (
                    <ul className={css(Styles.dropdownActions)}>
                        {languages.map(lan => {
                            if (!lan.enabled || !lan.inMenu) return null;
                            return (
                                <li
                                    key={lan.code}
                                    {...{
                                        className: css(Styles.dropdownAction),
                                        onClick: () => change(lan.code),
                                    }}
                                >
                                    {lan.name}
                                </li>
                            );
                        })}
                    </ul>
                ),
            }}
        />
    );
};

const MenuList = ({active, close}: {active?: string; close?: () => void}) => {
    const {t, tReady} = useLocalization('header');

    const open = (link: string) => (e: React.MouseEvent) => {
        e.preventDefault();
        if (close) {
            close();
            setTimeout(() => {
                history.push(link);
            }, 200);
        } else {
            history.push(link);
        }
    };

    const onClickSignout = (e: React.MouseEvent) => {
        e.preventDefault();
        SignOut();
    };

    if (!tReady) {
        return null;
    }

    const links: Array<{
        path: string;
        label: string;
        marker: string;
        is_divider?: boolean;
    }> = [
        {
            path: '',
            label: t('links.home'),
            marker: 'home',
        },
        {
            path: 'my-reservations',
            label: t('links.reservations'),
            marker: 'my-reservations',
        },
        {
            path: 'one-day-tours',
            label: t('links.oneTours'),
            marker: 'oneTours',
        },
        {
            path: 'bicycle-tours',
            label: t('links.bicycle'),
            marker: 'bicycle',
        },
        {
            path: 'multi-day-tours',
            label: t('links.multiTours'),
            marker: 'multiTours',
        },
        {
            path: 'events',
            label: t('links.events'),
            marker: 'events',
        },
        {
            path: 'fleet',
            label: t('links.fleet'),
            marker: 'fleet',
            is_divider: true,
        },
        // {
        //     path: 'sale',
        //     label: t('links.sale'),
        //     marker: 'sale',
        // },
        {
            path: 'franchising',
            label: t('links.franchising'),
            marker: 'franchising',
        },
        {
            path: 'faq',
            label: t('links.faq'),
            marker: 'faq',
            is_divider: true,
        },
        {
            path: 'work',
            label: t('links.work'),
            marker: 'work',
        },
        {
            path: 'contacts',
            label: t('links.contacts'),
            marker: 'contacts',
        },
    ];

    return (
        <ul className={css(Styles.nav)}>
            {links.map((link, index) => {
                return (
                    <li
                        className={css(Styles.navItem, link.is_divider && Styles.navItemDivider)}
                        key={index}
                    >
                        <a
                            href={GetPath(link.path)}
                            className={css(Styles.navItemLink, active === link.marker && Styles.navItemLinkActive)}
                            onClick={open(GetPath(link.path))}
                        >
                            <span>{link.label}</span>
                            <ArrowIcon
                                width="14"
                                height="14"
                            />
                        </a>
                    </li>
                );
            })}
            {session.user.is_verified && session.extra.is_superuser && (
                <li className={css(Styles.navItem, Styles.navItemDivider)}>
                    <a
                        href={GetPath('console')}
                        className={css(Styles.navItemLink)}
                        onClick={open(GetPath('console'))}
                    >
                        <span>Console</span>
                        <ArrowIcon
                            width="14"
                            height="14"
                        />
                    </a>
                </li>
            )}
            {session.user.is_verified && (
                <li className={css(Styles.navItem, Styles.navItemDivider)}>
                    <a
                        href={'/'}
                        className={css(Styles.navItemLink)}
                        onClick={onClickSignout}
                    >
                        <span>Sign out</span>
                        <ArrowIcon
                            width="14"
                            height="14"
                        />
                    </a>
                </li>
            )}
        </ul>
    );
};

const Styles = CreateSheet({
    header: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        display: 'flex',
        height: Sizes.header,
        alignItems: 'center',
        backgroundColor: PageBg,
        transition: 'background-color 0.25s ease-in 0s, box-shadow 0.25s ease-in 0s',
        zIndex: 20,
    },
    transparent: {
        background: 'transparent',
    },
    container: {
        display: 'flex',
        width: '100%',
        margin: '0 auto',
        padding: '0 50px',
        '@media(max-width: 900px)': {
            padding: '0 20px',
        },
    },
    col: {
        display: 'flex',
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    left: {
        justifyContent: 'flex-start',
    },
    center: {
        justifyContent: 'center',
        gap: 12,
    },
    mainMenu: {
        '@media(max-width: 750px)': {
            display: 'none',
        },
    },
    right: {
        justifyContent: 'flex-end',
    },
    nav: {
        display: 'block',
        margin: 0,
        padding: 0,
    },
    navItem: {
        margin: '0 0 12px 0',
        listStyle: 'none',
    },
    navItemDivider: {
        margin: '25px 0 12px 0',
    },
    navItemLink: {
        position: 'relative',
        display: 'flex',
        color: TextColor,
        textDecoration: 'none',
        fontSize: 16,
        fontWeight: 600,
        gap: 10,
        whiteSpace: 'nowrap',
        listStyle: 'none',
        userSelect: 'none',
        alignItems: 'center',
        ':nth-child(1n) svg': {
            display: 'none',
        },
        ':hover': {
            color: Accent1,
            ':nth-child(1n) svg': {
                display: 'block',
                fill: Accent1,
            },
        },
    },
    navItemLinkActive: {
        color: Accent1,
    },
    languageItem: {
        display: 'flex',
        marginLeft: 30,
        fontSize: 16,
        fontWeight: 600,
        color: TextColor,
        alignItems: 'center',
        cursor: 'pointer',
        ':hover': {
            color: Secondary,
            ':nth-child(1n) svg': {
                fill: Secondary,
            },
        },
    },
    languageItemTransparent: {
        color: WhiteText,
        ':hover': {
            color: WhiteText,
            ':nth-child(1n) svg': {
                fill: WhiteText,
            },
        },
    },
    languageIcon: {
        display: 'block',
        marginRight: 15,
        fill: TextColor,
    },
    languageIconTransparent: {
        fill: WhiteText,
    },
    dropdownActions: {
        position: 'absolute',
        top: 'calc(100% + 5px)',
        right: 0,
        minWidth: '100%',
        margin: 0,
        padding: 7,
        fontSize: '1rem',
        fontWeight: 600,
        listStyle: 'none',
        color: TextColor,
        background: ElementBg,
        borderRadius: 10,
        boxShadow: '0px 10px 20px rgba(22, 22, 22, 0.05)',
        border: `1px solid ${StrokeColor}`,
        userSelect: 'none',
        zIndex: 1,
    },
    dropdownAction: {
        padding: '5px 10px',
        whiteSpace: 'nowrap',
        borderRadius: 5,
        ':hover': {
            background: TextArea,
        },
    },
    menuToggle: {
        display: 'flex',
        width: 44,
        height: 44,
        alignItems: 'center',
        justifyContent: 'center',
        background: TextArea,
        borderRadius: '50%',
        cursor: 'pointer',
    },
    menuToggleTransparent: {
        background: 'transparent',
    },
    rightMenuIcon: {
        display: 'block',
        fill: TextColor,
    },
    rightMenu: {
        position: 'fixed',
        top: 10,
        right: -350,
        width: 350,
        bottom: 10,
        padding: '20px 25px 30px 25px',
        backgroundColor: ElementBg,
        boxShadow: '0px 10px 50px 0px rgba(0, 0, 0, 0.12)',
        borderRadius: 20,
        border: `1px solid ${LightGray}`,
        transform: 'translate(0, 0px)',
        zIndex: 1297,
        overflow: 'auto',
        transition: 'transform 0.2s ease-in-out',
        '@media(max-width: 500px)': {
            top: 0,
            right: '-100%',
            bottom: 0,
            width: 500,
            maxWidth: '100%',
            borderRadius: 0,
        },
    },
    rightMenuVisible: {
        transform: 'translate(-385px)',
        '@media(max-width: 500px)': {
            transform: 'translate(-100%)',
        },
    },
    rightMenuItem: {
        margin: 0,
        fontSize: 20,
        fontWeight: 600,
        color: LightGray,
    },
    rightMenuBtn: {
        display: 'block',
        width: '100%',
        marginTop: 12,
    },
    rightMenuBtnTop: {
        marginTop: 25,
    },
    menuTop: {
        display: 'flex',
        margin: '0 50px 25px 0',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    menuClose: {
        position: 'absolute',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        top: 20,
        right: 20,
        width: 30,
        height: 30,
        borderRadius: 3,
        cursor: 'pointer',
        ':hover': {
            backgroundColor: TextArea,
        },
    },
    menuCloseIcon: {
        display: 'block',
        fill: TextColor,
    },
    navItemArrow: {
        display: 'none',
        fill: Accent1,
    },
    languageMobile: {
        margin: '0 0 0 30px',
    },
    contactText: {
        margin: '0 0 15px 0',
    },
    contact: {
        marginTop: 25,
        padding: 30,
        fontSize: 18,
        fontWeight: 600,
        color: TextColor,
        textAlign: 'center',
        background: TextArea,
        borderRadius: 10,
    },
    phone: {
        fontSize: 22,
        color: TextColor,
        textDecoration: 'none',
    },
    contactLink: {
        color: TextColor,
        textDecoration: 'none',
    },
    social: {
        display: 'flex',
        margin: '25px 0 0 0',
        padding: 0,
        alignItems: 'center',
        justifyContent: 'center',
        listStyle: 'none',
    },
    socialItem: {
        margin: '0 20px',
    },
    socialItemLink: {
        display: 'block',
    },
    socialItemIcon: {
        display: 'block',
        fill: Primary,
    },
    language: {
        marginRight: 30,
        '@media(max-width: 600px)': {
            display: 'none',
        },
    },
    lngTitle: {
        textTransform: 'uppercase',
        transition: 'color 0.25s ease-in-out',
    },
    btn: {
        borderRadius: 40,
        padding: '10px 20px',
        whiteSpace: 'nowrap',
    },
});
